import { X, CalendarDays } from "lucide-react";

// iter87.199.150 — Outcome accent → chip classes. Mirrors the color
// keys `LeaderboardTable.buildMatchdayBreakdownRows()` assigns to each
// pick/column (best → worst: neon > cyber > volt > heat).
const CHIP_CLASSES = {
    neon: "border-neon/50 bg-neon/10 text-neon",
    cyber: "border-cyber/50 bg-cyber/10 text-cyber",
    volt: "border-volt/50 bg-volt/10 text-volt",
    heat: "border-heat/50 bg-heat/10 text-heat",
};

// iter87.199.147 → iter87.199.151 — Matchday Breakdown modal. One
// table row per COMPLETED matchday: label | team chips | one count
// column PER outcome type (WC / W+NC / LC / MISS, or W / D / L for
// classic pot templates) | points delta for that matchday only, plus
// a Total row. Mobile-first vertical scroll (rows stack).
export default function MatchdayBreakdownModal({
    open, onClose, playerName, rows = [], totalPoints, columns = [],
    title = "Matchday Breakdown", emptyLabel = "No matchdays completed yet.",
}) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="matchday-breakdown-modal-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-md w-full max-h-[85dvh] flex flex-col"
                data-testid="matchday-breakdown-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <CalendarDays className="w-5 h-5 text-cyber shrink-0" />
                        <div className="min-w-0">
                            <h2 className="font-display font-black text-lg uppercase truncate">{title}</h2>
                            <p className="text-[11px] text-zinc-500 truncate">{playerName}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-ghost p-1.5"
                        data-testid="matchday-breakdown-modal-close-btn"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Column header row — outcome columns are dynamic
                    (WC/W+NC/LC/MISS for spread templates, W/D/L for
                    classic pot templates) so the labels always match
                    what's actually in the data. */}
                {rows.length > 0 && (
                    <div className="flex items-center gap-1.5 px-5 pt-3 text-[9px] font-black uppercase tracking-widest text-zinc-500">
                        <span className="w-11 shrink-0">Wk</span>
                        <span className="flex-1">Teams</span>
                        {columns.map((c) => (
                            <span key={c.key} className={`w-9 text-center shrink-0 ${CHIP_CLASSES[c.color]?.split(" ")[2] || ""}`}>{c.label}</span>
                        ))}
                        <span className="w-9 text-right shrink-0">Pts</span>
                    </div>
                )}

                <div className="overflow-y-auto px-5 py-2 flex-1 space-y-1.5" data-testid="matchday-breakdown-list">
                    {rows.length === 0 && (
                        <p className="py-6 text-center text-xs text-zinc-500" data-testid="matchday-breakdown-empty">
                            {emptyLabel}
                        </p>
                    )}
                    {rows.map((r) => (
                        <div
                            key={r.label}
                            data-testid={`matchday-breakdown-row-${r.label}`}
                            className="flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2.5"
                        >
                            <span className="w-11 shrink-0 font-mono font-bold text-zinc-200 text-sm">{r.label}</span>
                            <div className="flex-1 flex flex-wrap gap-1">
                                {(r.picks || []).map((p, i) => (
                                    <span
                                        key={`${r.label}-${i}`}
                                        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono font-bold text-[10px] ${CHIP_CLASSES[p.color] || CHIP_CLASSES.volt}`}
                                        title={p.outcomeLabel}
                                    >
                                        {p.code}
                                    </span>
                                ))}
                                {(r.picks || []).length === 0 && (
                                    <span className="text-[11px] text-zinc-600">No picks</span>
                                )}
                            </div>
                            {columns.map((c) => (
                                <span key={c.key} className="w-9 shrink-0 text-center font-mono text-[11px] text-zinc-300">
                                    {(r.counts || {})[c.key] || 0}
                                </span>
                            ))}
                            <span className={`w-9 shrink-0 text-right font-mono font-bold text-sm ${r.points > 0 ? "text-cyber" : r.points < 0 ? "text-heat" : "text-zinc-500"}`}>
                                {r.points > 0 ? `+${r.points}` : r.points}
                            </span>
                        </div>
                    ))}
                </div>

                <div className="flex items-center justify-between px-5 py-3 border-t-2 border-[#222]">
                    <span className="font-black uppercase tracking-widest text-[10px] text-zinc-400">Total</span>
                    <span className="font-mono font-black text-base text-white" data-testid="matchday-breakdown-total">
                        {totalPoints > 0 ? `+${totalPoints}` : totalPoints}
                    </span>
                </div>
            </div>
        </div>
    );
}
